import { HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, tap, catchError, throwError } from 'rxjs';
import { ApiResponse } from '../../models';
import { ForgotPasswordRequestDTO, LoginRequestDTO, LoginResponseDTO, PreLoginRequestDTO, PreLoginResponseDTO, ResetPasswordRequestDTO } from '../../models/login.model';
import { LoggerService } from '../../services/logger.service';
import { LoginRepository } from '../contracts/login.repository';
import { BaseApiService } from '../../services/base-api.service';
import { withCaptcha } from '../../interceptors/captcha.context';

/**
 * Implementación de LoginRepository para servicios REST.
 */
@Injectable({
    providedIn: 'root',
})
export class LoginHttpRepository extends BaseApiService implements LoginRepository {
    private readonly controllerUrl = '/auth/';
    private readonly logger = inject(LoggerService);

    public login(
        request: LoginRequestDTO
    ): Observable<HttpResponse<ApiResponse<LoginResponseDTO>>> {
        return this.http.post<ApiResponse<LoginResponseDTO>>(
            `${this.baseUrl}${this.controllerUrl}login`,
            request,
            {
                observe: 'response',
                withCredentials: true,
            }
        ).pipe(
            tap(() => this.logger.info('Login HTTP Call Success', 'LoginHttpRepository')),
            catchError(error => {
                this.logger.error('Login HTTP Call Error', 'LoginHttpRepository', error);
                return throwError(() => error);
            })
        );
    }

    public refreshToken(): Observable<HttpResponse<ApiResponse<LoginResponseDTO>>> {
        return this.http.post<ApiResponse<LoginResponseDTO>>(
            `${this.baseUrl}${this.controllerUrl}refresh-token`,
            {},
            {
                observe: 'response',
                withCredentials: true,
            }
        ).pipe(
            tap(() => this.logger.info('Refresh token HTTP Call Success', 'LoginHttpRepository')),
            catchError(error => {
                this.logger.error('Refresh token HTTP Call Error', 'LoginHttpRepository', error);
                return throwError(() => error);
            })
        );
    }

    public logout(): Observable<HttpResponse<ApiResponse<boolean>>> {
        return this.http.post<ApiResponse<boolean>>(
            `${this.baseUrl}${this.controllerUrl}logout`,
            {},
            {
                observe: 'response',
                withCredentials: true,
            }
        );
    }

    public preLogin(
        request: PreLoginRequestDTO
    ): Observable<HttpResponse<ApiResponse<PreLoginResponseDTO>>> {
        return this.http.post<ApiResponse<PreLoginResponseDTO>>(
            `${this.baseUrl}${this.controllerUrl}pre-login`,
            request,
            { observe: 'response'}
        );
    }

    public resetPassword(
        request: ResetPasswordRequestDTO
    ): Observable<HttpResponse<ApiResponse<string>>> {
        return this.http.post<ApiResponse<string>>(
            `${this.baseUrl}${this.controllerUrl}reset-password`,
            request,
            {
                observe: 'response',
                context: withCaptcha()
            }
        );
    }

    public forgotPassword(
        request: ForgotPasswordRequestDTO
    ): Observable<HttpResponse<ApiResponse<string>>> {
        return this.http.post<ApiResponse<string>>(
            `${this.baseUrl}${this.controllerUrl}forgot-password`,
            request,
            {
                observe: 'response',
                context: withCaptcha()
            }
        );
    }
}
